import React, { useState, useEffect } from "react";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/airbnb.css";
import { API_ROUTES } from "@/api/routes";

const TIPOS = ["Movil", "Fijo", "Buzon", "Flat"];

const pad2 = (n) => String(n).padStart(2, "0");
const toFlatpickrStr = (dIn = new Date()) => {
    const d = typeof dIn === "string" ? new Date(dIn.replace("T", " ")) : dIn;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const BLANK_ROW = () => ({
    tipo_llamada: "",
    rango_min_inicio: "",
    rango_min_fin: "",
    tarifa: "",
    fecha_inicio: toFlatpickrStr(new Date()),
});

function Row({ value, onChange, onRemove }) {
    const fechaValue = value?.fecha_inicio
        ? String(value.fecha_inicio).replace("T", " ").slice(0, 16)
        : toFlatpickrStr();

    const isFlat = value.tipo_llamada === "Flat";

    return (
        <div className="rounded border p-3 bg-white">
            <div className="grid sm:grid-cols-5 gap-2 items-end">
                <div>
                    <label className="block text-xs text-slate-600">Tipo</label>
                    <select
                        className="w-full rounded border px-2 py-1.5"
                        value={value.tipo_llamada}
                        onChange={(e) => onChange({ ...value, tipo_llamada: e.target.value })}
                    >
                        <option value="">—</option>
                        {TIPOS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs text-slate-600">Rango mínimo (inicio)</label>
                    <input
                        type="number"
                        className="w-full rounded border px-2 py-1.5"
                        value={value.rango_min_inicio}
                        onChange={(e) => onChange({ ...value, rango_min_inicio: e.target.value })}
                        placeholder="1"
                        min={0}
                    />
                </div>

                <div>
                    <label className="block text-xs text-slate-600">Rango mínimo (fin)</label>
                    <input
                        type="number"
                        className="w-full rounded border px-2 py-1.5"
                        value={value.rango_min_fin}
                        onChange={(e) => onChange({ ...value, rango_min_fin: e.target.value })}
                        placeholder="1000000"
                        min={0}
                    />
                </div>

                <div>
                    <label className="block text-xs text-slate-600">Tarifa</label>
                    <input
                        type="number"
                        step="0.0001"
                        className="w-full rounded border px-2 py-1.5"
                        value={value.tarifa}
                        onChange={(e) => onChange({ ...value, tarifa: e.target.value })}
                        placeholder="0.0900"
                    />
                </div>

                <div className="flex gap-2 items-end">
                    <div className="flex-1">
                        <label className="block text-xs text-slate-600">Inicio (24h)</label>
                        <Flatpickr
                            className="w-full rounded border px-2 py-1.5"
                            value={fechaValue}
                            options={{
                                enableTime: true,
                                time_24hr: true,
                                dateFormat: "Y-m-d H:i",
                                allowInput: true,
                                defaultDate: fechaValue || new Date(),
                            }}
                            onChange={(_dates, dateStr) => {
                                onChange({ ...value, fecha_inicio: dateStr });
                            }}
                        />
                    </div>
                    <button type="button" onClick={onRemove} className="text-xs text-rose-600">
                        quitar
                    </button>
                </div>
            </div>

            {isFlat && (
                <div className="mt-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                    FLAT: cada mes se suman los minutos Móvil+Fijo; se aplica la tarifa del rango alcanzado.
                </div>
            )}
        </div>
    );
}

export default function TarifasPerfilCard({
    rows,
    setRows,
    title = "Perfiles de Tarifas",
    onSubmit,
    submitting = false,
    canSubmit = true,
    onModoChange,
    onPerfilSeleccionadoChange,
}) {
    // Estados para perfil
    const [modoPerfil, setModoPerfil] = useState("nuevo");
    const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);
    const [perfilesExistentes, setPerfilesExistentes] = useState([]);
    const [cargandoPerfiles, setCargandoPerfiles] = useState(false);

    // Estados para nuevo perfil
    const [nombre, setNombre] = useState("");
    const [descripcion, setDescripcion] = useState("");

    // Notificar al padre cuando cambia el modo
    useEffect(() => {
        if (onModoChange) {
            onModoChange(modoPerfil);
        }
    }, [modoPerfil]);

    // Notificar al padre cuando cambia el perfil seleccionado
    useEffect(() => {
        const perfilId = perfilSeleccionado?.value || null;
        if (onPerfilSeleccionadoChange) {
            onPerfilSeleccionadoChange(perfilId);
        }
    }, [perfilSeleccionado]);

    // Cargar perfiles existentes
    useEffect(() => {
        const cargarPerfiles = async () => {
            setCargandoPerfiles(true);
            try {
                const response = await fetch(API_ROUTES.listadoPerfilesTarifas, {
                    credentials: 'include'
                });
                const data = await response.json();
                setPerfilesExistentes(data || []);
            } catch (err) {
                console.error('Error cargando perfiles:', err);
            } finally {
                setCargandoPerfiles(false);
            }
        };
        cargarPerfiles();
    }, []);

    const addRow = () => setRows((p) => [...p, BLANK_ROW()]);
    const updateRow = (i, v) => setRows((p) => p.map((r, idx) => (idx === i ? v : r)));
    const removeRow = (i) =>
        setRows((p) => {
            const next = p.filter((_, idx) => idx !== i);
            return next.length ? next : [BLANK_ROW()];
        });

    const anyFlat = rows.some((r) => r.tipo_llamada === "Flat");

    // Convertir perfiles a opciones para react-select
    const opcionesPerfiles = perfilesExistentes.map((p) => ({
        value: p.id,  // ⚠️ Usar "id" no "perfil_id"
        label: `${p.nombre}${p.descripcion ? ` - ${p.descripcion}` : ''}`,
        perfil: p
    }));

    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
            boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
            "&:hover": {
                borderColor: "#9ca3af",
            },
            padding: "2px",
        }),
        menu: (base) => ({
            ...base,
            zIndex: 50,
        }),
    };

    // Determinar si se puede hacer submit
    const puedeSubmitear = modoPerfil === "nuevo"
        ? canSubmit && nombre.trim().length > 0
        : canSubmit && perfilSeleccionado !== null;

    const handleSubmit = () => {
        if (modoPerfil === "existente") {
            onSubmit?.({
                modo: "existente",
                perfil_id: perfilSeleccionado?.value
            });
        } else {
            const items = rows.map((r) => ({
                tipo_llamada: r.tipo_llamada,
                rango_min_inicio: Number(r.rango_min_inicio),
                rango_min_fin: Number(r.rango_min_fin),
                tarifa: Number(r.tarifa),
                fecha_inicio: String(r.fecha_inicio || "").replace("T", " "),
            }));

            onSubmit?.({
                modo: "nuevo",
                items,
                nombre: nombre?.trim() || null,
                descripcion: descripcion?.trim() || null
            });
        }
    };

    // Handler para el cambio del Select
    const handlePerfilChange = (selectedOption) => {
        setPerfilSeleccionado(selectedOption);
    };

    return (
        <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
            <div className="w-full px-3 py-2 border-b bg-slate-50">
                <div className="font-semibold text-slate-800 text-sm">{title}</div>
                <div className="text-[11px] text-slate-500">
                    {modoPerfil === "nuevo"
                        ? "Asigna un nombre/descripcion al conjunto y agrega sus filas (todas compartirán el mismo perfil_id)."
                        : "Selecciona un perfil existente para usar sus tarifas configuradas."}
                </div>
            </div>

            {/* Selector de modo: Nuevo perfil o Existente */}
            <div className="px-4 pt-4 pb-3 border-b bg-slate-50">
                <label className="block text-xs font-medium text-slate-700 mb-2">
                    ¿Crear nuevo perfil o usar uno existente?
                </label>
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            value="nuevo"
                            checked={modoPerfil === "nuevo"}
                            onChange={(e) => {
                                setModoPerfil(e.target.value);
                                setPerfilSeleccionado(null);
                            }}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm text-slate-700">Nuevo perfil</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            value="existente"
                            checked={modoPerfil === "existente"}
                            onChange={(e) => {
                                setModoPerfil(e.target.value);
                                setNombre("");
                                setDescripcion("");
                            }}
                            className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm text-slate-700">Perfil existente</span>
                    </label>
                </div>
            </div>

            {/* Formulario según el modo seleccionado */}
            {modoPerfil === "nuevo" ? (
                <>
                    <div className="px-4 pt-4">
                        <div className="grid sm:grid-cols-2 gap-3 pb-3">
                            <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">
                                    Nombre del perfil
                                </label>
                                <input
                                    type="text"
                                    className="w-full rounded border px-2 py-1.5"
                                    value={nombre}
                                    onChange={(e) => setNombre(e.target.value)}
                                    placeholder="Tarifa México F0.95 M0.15 B0.10"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">
                                    Descripción
                                </label>
                                <input
                                    type="text"
                                    className="w-full rounded border px-2 py-1.5"
                                    value={descripcion}
                                    onChange={(e) => setDescripcion(e.target.value)}
                                    placeholder="Descripción corta opcional"
                                />
                            </div>
                        </div>
                    </div>

                    {anyFlat && (
                        <div className="mx-4 mt-0 mb-0 px-3 py-2 rounded bg-blue-50 text-[12px] text-blue-800 border border-blue-100">
                            FLAT: se suman minutos Móvil+Fijo mensuales; se aplica el rango superado.
                        </div>
                    )}

                    <div className="p-4 space-y-3">
                        {rows.map((r, i) => (
                            <div key={i}>
                                <div className="text-xs text-slate-500 mb-2">Tarifa #{i + 1}</div>
                                <Row value={r} onChange={(v) => updateRow(i, v)} onRemove={() => removeRow(i)} />
                            </div>
                        ))}

                        <button
                            type="button"
                            className="w-full px-3 py-2 text-sm border-2 border-dashed rounded hover:bg-slate-50"
                            onClick={addRow}
                        >
                            + Agregar tarifa
                        </button>
                    </div>
                </>
            ) : (
                <div className="p-4">
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                        Seleccionar perfil existente
                    </label>
                    <Select
                        options={opcionesPerfiles}
                        value={perfilSeleccionado}
                        onChange={handlePerfilChange}
                        placeholder="— Buscar perfil —"
                        isClearable
                        isSearchable
                        isLoading={cargandoPerfiles}
                        noOptionsMessage={() => "No hay perfiles disponibles"}
                        styles={customStyles}
                        className="text-sm"
                    />
                    {perfilSeleccionado && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm font-medium text-blue-900">
                                Perfil #{perfilSeleccionado.perfil.id}: {perfilSeleccionado.perfil.nombre}
                            </p>
                            {perfilSeleccionado.perfil.descripcion && (
                                <p className="text-xs text-blue-700 mt-1">
                                    {perfilSeleccionado.perfil.descripcion}
                                </p>
                            )}
                            <p className="text-xs text-slate-600 mt-2">
                                ✓ Este perfil ya tiene sus tarifas configuradas
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Botón de submit */}
            <div className="px-4 pb-4 flex justify-end">
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !puedeSubmitear}
                    className={`px-4 py-2 text-sm rounded transition-colors ${submitting || !puedeSubmitear
                        ? 'bg-blue-400 text-white cursor-not-allowed opacity-50'
                        : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                        }`}
                    title={
                        submitting ? 'Guardando...' :
                            !canSubmit ? 'Completa los campos requeridos de empresa y cliente' :
                                modoPerfil === "existente" && !perfilSeleccionado ? 'Selecciona un perfil' :
                                    modoPerfil === "nuevo" && !nombre.trim() ? 'Ingresa nombre del perfil' :
                                        'Guardar registro'
                    }
                >
                    {submitting
                        ? "Guardando..."
                        : "Guardar registro"}
                </button>
            </div>
        </div>
    );
}